import { lazy, Suspense, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { CreditCard, Plus, Package, FileText, ExternalLink, AlertCircle, CheckCircle2, Settings as SettingsIcon, BarChart3, ShieldCheck, ShieldAlert, CalendarRange, Download, Loader2, X } from "lucide-react";

// Lazy so the recharts dependency only loads when the user actually opens
// the Reports tab. Without this, every visit to /payments paid for the
// chart library even though most sessions never look at the report tab.
const ReportsPage = lazy(() => import("./ReportsPage").then((m) => ({ default: m.ReportsPage })));
import { Avatar, Badge, Button, EmptyState, Input, Label, Modal, ModalFooter, Paginator, Select, Skeleton, useToast, useConfirm } from "@/components/ui";
import { api, ApiError } from "@/lib/api";
import { formatMoney, formatRelative, formatDateTime } from "@/lib/format";
import { SmartListPicker } from "@/features/lists/SmartListPicker";
import { useSmartList, smartListQueryParams } from "@/features/lists/SmartListContext";
import type { Invoice, Payment, PaymentProviderConfig, Product } from "@/lib/types";
import { cn } from "@/lib/utils";
import { canAny, useMe, type Capability } from "@/lib/me";

type Tab = "payments" | "invoices" | "monthly" | "reports" | "settings";
/**
 * "Front desk should have access to … sales" and, the day before, "the
 * summaries of moneys the accounting reports should not be available to
 * everyone. Just me."
 *
 * Both are true at once: the desk reads what was sold and cancels a bill
 * that was raised in error, while the accountant's monthly reconciliation
 * and the revenue reports stay behind Reports.
 */
const TABS: { k: Tab; l: string; Icon: React.ComponentType<{ className?: string }>; needs: Capability[] }[] = [
  { k: "payments", l: "Payments",  Icon: CreditCard, needs: ["reports", "checkout"] },
  { k: "invoices", l: "Invoices",  Icon: FileText,   needs: ["reports", "checkout"] },
  // "he specifically needs this report in the new software" - her accountant's
  // monthly reconciliation, in the same place Boulevard kept it.
  { k: "monthly",  l: "Monthly activity", Icon: CalendarRange, needs: ["reports"] },
  { k: "reports",  l: "Reports",   Icon: BarChart3,  needs: ["reports"] },
  { k: "settings", l: "Provider",  Icon: SettingsIcon, needs: ["settings"] },
];

export function PaymentsPage() {
  const navigate = useNavigate();
  const me = useMe();
  const { tab: tabParam } = useParams<{ tab?: string }>();
  // A tab this person cannot open falls back to the sales list rather than
  // rendering an empty page.
  const allowed = TABS.filter((t) => canAny(me, t.needs));
  const tab: Tab = (allowed.some((t) => t.k === tabParam) ? tabParam : "payments") as Tab;
  const [config, setConfig] = useState<PaymentProviderConfig | null>(null);

  useEffect(() => {
    api.get<{ config: PaymentProviderConfig | null }>("/payments/config")
      .then((d) => setConfig(d.config))
      .catch(() => {});
  }, []);

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold text-zinc-100 sm:text-2xl">Payments</h1>
          <p className="mt-1 text-sm text-zinc-500">
            Charge customers, manage products, and track invoices.
            {!config?.enabled && <span className="ml-2 text-amber-300">Connect a provider to start collecting payments.</span>}
          </p>
        </div>
        {config?.enabled && (
          <Badge color={config.mode === "live" ? "emerald" : "amber"}>
            {config.provider} · {config.mode === "live" ? "Live mode" : "Test mode"}
          </Badge>
        )}
      </div>

      {/* Mobile-friendly: chips evenly distributed (flex-1) on phones, label
          + icon at sm+. URL-driven so /payments/<tab> deep-links. */}
      <div className="flex gap-1 border-b border-zinc-800">
        {TABS.filter((t) => canAny(me, t.needs)).map(({ k, l, Icon }) => (
          <button
            key={k}
            type="button"
            onClick={() => navigate(k === "payments" ? "/payments" : `/payments/${k}`)}
            title={l}
            className={cn(
              "inline-flex flex-1 sm:flex-initial items-center justify-center gap-2 border-b-2 px-2 sm:px-3 py-2 text-sm font-medium transition-colors",
              tab === k ? "border-indigo-500 text-zinc-100" : "border-transparent text-zinc-500 hover:text-zinc-300"
            )}
          >
            <Icon className="h-4 w-4" />
            <span className="hidden sm:inline">{l}</span>
          </button>
        ))}
      </div>

      {tab === "payments" && <PaymentsTab />}
      {tab === "invoices" && <InvoicesTab />}
      {tab === "monthly"  && canAny(me, ["reports"]) && <MonthlyActivityTab />}
      {tab === "reports"  && canAny(me, ["reports"]) && <Suspense fallback={<Skeleton className="h-96" />}><ReportsPage /></Suspense>}
      {tab === "settings" && <ProviderSettingsTab onChanged={(c) => setConfig(c)} />}
    </div>
  );
}

// ─── Monthly activity ──────────────────────────────────────────────────────
/**
 * What her accountant asks for every month.
 *
 * "I use this report every month. It reports every charge and the fees
 *  associated with that charge. I can match those to the deposits hitting
 *  QBO."
 *
 * The figures come from the card processor itself, because the fee is taken
 * after the charge and only the processor knows it. He does not need an
 * account here or at Stripe - she downloads the file and sends it.
 */
interface MonthRow {
  month: string;
  label: string;
  settled_charges_cents: number;
  settled_refunds_cents: number;
  fees_cents: number;
  net_settled_cents: number;
  adjustments_cents: number;
  payouts: number;
  closed: boolean;
}

function MonthlyActivityTab() {
  const toast = useToast();
  const [months, setMonths] = useState<MonthRow[] | null>(null);
  const [reason, setReason] = useState<string | null>(null);
  const [note, setNote] = useState<{ note: string; tone: "info" | "warn" } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    api.get<{
      months: MonthRow[]; available: boolean; reason?: string;
      deposit_note?: { note: string; tone: "info" | "warn" } | null;
    }>("/payments/monthly-activity", { months: 13 })
      .then((d) => {
        setMonths(d.months);
        setReason(d.available ? null : (d.reason ?? null));
        setNote(d.deposit_note ?? null);
      })
      .catch((err) => {
        setMonths([]);
        setReason(err instanceof ApiError ? err.message : "Couldn't load those figures");
      });
  }, []);

  /** Pull the file through the api so it carries the login, then save it. */
  const download = async (m: MonthRow) => {
    setBusy(m.month);
    try {
      const res = await fetch(`/api/payments/monthly-activity/${m.month}.csv`, { credentials: "include" });
      if (!res.ok) throw new Error(`That month wouldn't download (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `heavenly-hydration-${m.month}-charges-and-fees.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Download failed");
    } finally {
      setBusy(null);
    }
  };

  if (!months) return <Skeleton className="h-72" />;

  if (reason) {
    return <EmptyState icon={CalendarRange} title="Nothing to report yet" description={reason} />;
  }

  return (
    <div className="space-y-3">
      <div className="rounded-xl border border-zinc-800 bg-zinc-900/30 px-4 py-3">
        <p className="text-sm text-zinc-200">Every charge, its fee, and the deposit it landed in.</p>
        <p className="mt-1 text-xs text-zinc-400">
          Download a month and send it to your accountant. Each line shows what was
          charged, what the card fee was, what was left, and which bank deposit it
          travelled in, so it can be matched to your bank statement and QuickBooks.
          Nobody needs an account here to read it.
        </p>
      </div>

      {/* An empty Deposit column reads as a fault, so say which it is. */}
      {note && (
        <div className={cn(
          "flex items-start gap-2 rounded-xl border px-4 py-3 text-xs",
          note.tone === "warn"
            ? "border-amber-500/40 bg-amber-500/10 text-amber-200"
            : "border-zinc-800 bg-zinc-900/30 text-zinc-300",
        )}>
          <AlertCircle className="mt-px h-3.5 w-3.5 shrink-0" />
          <span>{note.note}</span>
        </div>
      )}

      <div className="overflow-x-auto rounded-xl border border-zinc-800 bg-zinc-900/30">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-zinc-800 text-left text-xs uppercase tracking-wide text-zinc-500">
              <th className="px-3 py-2.5 font-medium">Month</th>
              <th className="px-3 py-2.5 font-medium text-right">Charges</th>
              <th className="px-3 py-2.5 font-medium text-right">Refunds</th>
              <th className="px-3 py-2.5 font-medium text-right">Card fees</th>
              <th className="px-3 py-2.5 font-medium text-right">Net</th>
              <th className="px-3 py-2.5 font-medium text-right">Adjustments</th>
              <th className="px-3 py-2.5 font-medium text-right">Deposits</th>
              <th className="px-3 py-2.5" />
            </tr>
          </thead>
          <tbody>
            {months.map((m) => (
              <tr key={m.month} className="border-b border-zinc-800/50 last:border-0">
                <td className="px-3 py-2.5 text-zinc-200">
                  {m.label}
                  {!m.closed && (
                    <span className="ml-2 rounded bg-amber-500/15 px-1.5 py-px text-[10px] font-medium text-amber-300">
                      still running
                    </span>
                  )}
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums text-zinc-200">{formatMoney(m.settled_charges_cents)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums text-zinc-400">{formatMoney(m.settled_refunds_cents)}</td>
                {/* Already negative, the way his old report showed it:
                    charges plus fees equals net, no sign to flip. */}
                <td className="px-3 py-2.5 text-right tabular-nums text-rose-300">{formatMoney(m.fees_cents)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums font-medium text-zinc-100">{formatMoney(m.net_settled_cents)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums text-zinc-400">{formatMoney(m.adjustments_cents)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums text-zinc-400">{m.payouts}</td>
                <td className="px-3 py-2.5 text-right">
                  <Button variant="secondary" size="sm" onClick={() => void download(m)} disabled={busy === m.month}>
                    {busy === m.month
                      ? <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />Building…</>
                      : <><Download className="mr-1.5 h-3.5 w-3.5" />Download CSV</>}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-zinc-500">
        Figures come from your card processor, so they match what actually reached
        your bank rather than what was rung up. A month still running will keep
        changing until it ends.
      </p>
    </div>
  );
}

// ─── Invoices tab ──────────────────────────────────────────────────────────
const INVOICES_PAGE_SIZE = 50;

function InvoicesTab() {
  const toast = useToast();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const { listId, filter: smartFilter, set: setSmartList } = useSmartList();

  const load = async () => {
    setLoading(true);
    try {
      const d = await api.get<{ invoices: Invoice[]; total: number }>(
        "/payments/invoices",
        { ...smartListQueryParams(listId, smartFilter), limit: INVOICES_PAGE_SIZE, offset }
      );
      setInvoices(d.invoices);
      setTotal(d.total ?? d.invoices.length);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed");
    } finally { setLoading(false); }
  };
  // Reset to page 0 when the filter changes — otherwise the user could
  // land on offset 200 of a freshly-narrowed result set that has 12 rows
  // and see an empty page with no obvious way back.
  useEffect(() => { setOffset(0); }, [listId, smartFilter]);
  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [listId, smartFilter, offset]);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-end">
        <SmartListPicker
          listId={listId}
          filter={smartFilter}
          onChange={(lid, f) => setSmartList(lid, f)}
        />
      </div>
      {loading ? <div className="space-y-2">{Array.from({length:5}).map((_,i)=><Skeleton key={i} className="h-16"/>)}</div>
       : invoices.length === 0
       ? <EmptyState icon={FileText} title="No invoices match" description="Invoices you create on a contact's page will show up here." />
       : <InvoicesTable invoices={invoices} onChanged={() => void load()} />}
      <Paginator total={total} limit={INVOICES_PAGE_SIZE} offset={offset} onChange={setOffset} />
    </div>
  );
}

function InvoicesTable({ invoices, onChanged }: { invoices: Invoice[]; onChanged: () => void }) {
  const toast = useToast();
  const confirm = useConfirm();
  const [busy, setBusy] = useState<string | null>(null);

  /* "how do I cancel open invoices that are errors" — a bill nobody is going
     to pay, closed, with its payment link killed at the same time so it can
     never be paid by accident afterwards. */
  const cancel = async (inv: Invoice) => {
    const ok = await confirm({
      title: "Cancel this bill?",
      description: `${formatMoney(inv.total_cents, inv.currency)}${inv.contact_name ? ` for ${inv.contact_name}` : ""}. Nobody can pay it afterwards, and any payment link on it stops working.`,
      confirmLabel: "Cancel bill",
      danger: true,
    });
    if (!ok) return;
    setBusy(inv.id);
    try {
      await api.post(`/payments/invoices/${inv.id}/void`, {});
      toast.success("Bill cancelled");
      onChanged();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "That didn't work");
    } finally { setBusy(null); }
  };

  return (
    <div className="overflow-hidden rounded-xl border border-zinc-800/70 bg-zinc-900/30">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-zinc-800/70 bg-zinc-900/40 text-xs uppercase tracking-wider text-zinc-500">
            <th className="px-3 py-2.5 text-left font-medium">Status</th>
            <th className="px-3 py-2.5 text-left font-medium">Contact</th>
            <th className="px-3 py-2.5 text-left font-medium">Amount</th>
            <th className="px-3 py-2.5 text-left font-medium">Created</th>
            <th className="w-20" />
          </tr>
        </thead>
        <tbody>
          {invoices.map((inv) => (
            <tr key={inv.id} className="group border-b border-zinc-800/40 hover:bg-zinc-900/30">
              <td className="px-3 py-2.5"><InvoiceStatus status={inv.status} /></td>
              <td className="px-3 py-2.5">
                {inv.contact_id ? (
                  <Link to={`/contacts/${inv.contact_id}`} className="text-zinc-200 hover:text-indigo-300">{inv.contact_name ?? "Unknown"}</Link>
                ) : <span className="text-zinc-500">—</span>}
              </td>
              <td className="px-3 py-2.5 font-medium text-zinc-200 tabular-nums">{formatMoney(inv.total_cents, inv.currency)}</td>
              <td className="px-3 py-2.5 text-zinc-500">{formatRelative(inv.created_at)}</td>
              <td className="px-3 py-2.5 text-right">
                {inv.hosted_url && (
                  <a href={inv.hosted_url} target="_blank" rel="noreferrer" className="inline-flex h-7 w-7 items-center justify-center rounded text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200" aria-label="Open hosted invoice">
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                )}
                {(inv.status === "open" || inv.status === "draft") && (
                  <button
                    type="button"
                    disabled={busy === inv.id}
                    onClick={() => void cancel(inv)}
                    title="Cancel this bill"
                    aria-label="Cancel this bill"
                    className="inline-flex h-7 w-7 items-center justify-center rounded text-zinc-400 hover:bg-zinc-800 hover:text-rose-300 disabled:opacity-50"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function InvoiceStatus({ status }: { status: Invoice["status"] }) {
  const map: Record<Invoice["status"], { color: string; label: string }> = {
    draft:          { color: "zinc",    label: "Draft" },
    open:           { color: "indigo",  label: "Open" },
    paid:           { color: "emerald", label: "Paid" },
    uncollectible:  { color: "rose",    label: "Uncollectible" },
    void:           { color: "zinc",    label: "Void" },
  };
  const v = map[status];
  return <Badge color={v.color}>{v.label}</Badge>;
}

// ─── Payments tab ─────────────────────────────────────────────────────────
const PAYMENTS_PAGE_SIZE = 50;

function PaymentsTab() {
  const toast = useToast();
  const [payments, setPayments] = useState<Payment[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    api.get<{ payments: Payment[]; total: number }>("/payments", { limit: PAYMENTS_PAGE_SIZE, offset })
      .then((d) => { setPayments(d.payments); setTotal(d.total ?? d.payments.length); })
      .catch((err) => toast.error(err instanceof ApiError ? err.message : "Failed"))
      .finally(() => setLoading(false));
  }, [toast, offset]);

  if (loading) return <div className="space-y-2">{Array.from({length:5}).map((_,i)=><Skeleton key={i} className="h-14"/>)}</div>;
  if (payments.length === 0) {
    return <EmptyState icon={CreditCard} title="No payments yet" description="When customers pay an invoice, the payment will appear here." />;
  }
  return (
    <div className="space-y-3">
      <div className="overflow-hidden rounded-xl border border-zinc-800/70 bg-zinc-900/30">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-zinc-800/70 bg-zinc-900/40 text-xs uppercase tracking-wider text-zinc-500">
              <th className="px-3 py-2.5 text-left font-medium">Status</th>
              <th className="px-3 py-2.5 text-left font-medium">Contact</th>
              <th className="px-3 py-2.5 text-left font-medium">Amount</th>
              <th className="px-3 py-2.5 text-left font-medium">Method</th>
              <th className="px-3 py-2.5 text-left font-medium">When</th>
            </tr>
          </thead>
          <tbody>
            {payments.map((p) => (
              <tr key={p.id} className="border-b border-zinc-800/40 hover:bg-zinc-900/30">
                <td className="px-3 py-2.5">
                  {p.status === "succeeded" && <Badge color="emerald" size="sm"><CheckCircle2 className="h-3 w-3 mr-1" />Succeeded</Badge>}
                  {p.status === "failed"    && <Badge color="rose" size="sm"><AlertCircle className="h-3 w-3 mr-1" />Failed</Badge>}
                  {p.status === "pending"   && <Badge color="amber" size="sm">Pending</Badge>}
                  {p.status === "refunded"  && <Badge color="zinc" size="sm">Refunded</Badge>}
                </td>
                <td className="px-3 py-2.5">{p.contact_id ? <Link to={`/contacts/${p.contact_id}`} className="text-zinc-200 hover:text-indigo-300">{p.contact_name}</Link> : "—"}</td>
                <td className="px-3 py-2.5 font-medium text-zinc-200 tabular-nums">{formatMoney(p.amount_cents, p.currency)}</td>
                <td className="px-3 py-2.5 text-xs text-zinc-500">
                  {p.kind === "manual" ? "Manual" : (p.payment_method.brand && p.payment_method.last4) ? `${p.payment_method.brand} •••• ${p.payment_method.last4}` : p.provider}
                </td>
                <td className="px-3 py-2.5 text-zinc-500">{formatRelative(p.charged_at ?? p.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Paginator total={total} limit={PAYMENTS_PAGE_SIZE} offset={offset} onChange={setOffset} />
    </div>
  );
}

// Products moved to Manage → Menu & seats → Products (with stock counts and
// the web shop), so there's one place to edit them.

// ─── Provider settings tab ────────────────────────────────────────────────
/**
 * Stripe (and any other payment provider) is connected at the SyncSuite
 * account level via the Connections UI, NOT here. The credentials live in
 * the platform connection store and are injected for billable Stripe
 * service calls. This page just shows status + non-credential preferences
 * (mode, default currency, on/off) and links to the upstream Connections
 * page when the user wants to (re)connect.
 */
function ProviderSettingsTab({ onChanged }: { onChanged: (c: PaymentProviderConfig) => void }) {
  const toast = useToast();
  const [config, setConfig] = useState<PaymentProviderConfig | null>(null);
  const [providers, setProviders] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [webhook, setWebhook] = useState<{ url: string; checked_at: string | null } | null>(null);

  const load = async () => {
    try {
      const d = await api.get<{
        config: PaymentProviderConfig | null;
        supported_providers: string[];
        webhook?: { url: string; checked_at: string | null } | null;
      }>("/payments/config");
      setConfig(d.config);
      setProviders(d.supported_providers);
      setWebhook(d.webhook ?? null);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed");
    }
  };
  useEffect(() => { void load(); }, []);

  const save = async () => {
    if (!config) return;
    setSaving(true);
    try {
      // `enabled` mirrors connection state — if the user has connected the
      // provider in their SyncSuite dashboard we treat it as on, otherwise
      // off. Surfacing this as a separate checkbox in the UI was confusing.
      const connectedNow = Object.values(config.config_mask).some(Boolean);
      await api.patch("/payments/config", {
        provider: config.provider,
        mode: config.mode,
        enabled: connectedNow,
        default_currency: config.default_currency,
      });
      toast.success("Saved");
      await load();
      if (config) onChanged({ ...config, enabled: connectedNow });
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed");
    } finally { setSaving(false); }
  };

  const test = async () => {
    setTesting(true);
    try {
      const r = await api.post<{ ok: boolean; error?: string }>("/payments/test-connection");
      if (r.ok) toast.success("Connection OK");
      else toast.error(r.error ?? "Connection failed");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed");
    } finally { setTesting(false); }
  };

  if (!config) return <Skeleton className="h-64" />;

  // We treat any field on `config_mask` as "credentials present in the
  // upstream connection." If none are set, the user hasn't connected yet
  // at the SyncSuite Connections layer.
  const connected = Object.values(config.config_mask).some(Boolean);
  const providerName = config.provider.charAt(0).toUpperCase() + config.provider.slice(1);

  return (
    <div className="lg:max-w-2xl space-y-4">
      <p className="text-sm text-zinc-400">
        Choose how you want to charge customers. Currently we support {providers.join(", ")}.
        To swap providers later, the AI just edits the active provider here — your invoices, products, and customers stay in place.
      </p>

      {/* Connection status — sourced from the SyncSuite Connections page,
          not entered here. */}
      <div className={cn(
        "flex items-start gap-3 rounded-xl border p-4",
        connected ? "border-emerald-500/30 bg-emerald-500/5" : "border-amber-500/30 bg-amber-500/5",
      )}>
        <div className={cn(
          "flex h-9 w-9 items-center justify-center rounded-lg shrink-0",
          connected ? "bg-emerald-500/15 text-emerald-300" : "bg-amber-500/15 text-amber-300",
        )}>
          {connected ? <ShieldCheck className="h-4 w-4" /> : <ShieldAlert className="h-4 w-4" />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm font-medium text-zinc-100">{providerName} connection</p>
            <Badge color={connected ? "emerald" : "amber"} size="xs">
              {connected ? "Connected via SyncSuite" : "Not connected"}
            </Badge>
          </div>
          <p className="mt-1 text-xs text-zinc-400">
            {connected
              ? `Your ${providerName} credentials live in your SyncSuite account's Connections, not in the CRM. To rotate keys or disconnect, open SyncSuite Connections in your dashboard.`
              : `To start collecting payments, open SyncSuite Connections in your dashboard and connect ${providerName}. The CRM will pick up the credentials automatically — no copy-pasting keys here.`}
          </p>
          {connected && config.provider === "stripe" && (
            <p className="mt-1.5 text-xs text-zinc-400">
              {webhook
                ? "Payment updates: on. Stripe tells the CRM the moment a payment link is paid, a membership renews, or a card is declined."
                : "Payment updates: setting up. Until then, paid links and renewals are picked up by a check every 15 minutes."}
            </p>
          )}
        </div>
      </div>

      <div className="rounded-xl border border-zinc-800/70 bg-zinc-900/30 p-5 space-y-3">
        <h3 className="text-sm font-semibold text-zinc-200">Preferences</h3>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <Label>Provider</Label>
            <Select value={config.provider} onChange={(e) => setConfig({ ...config, provider: e.target.value })}>
              {providers.map((p) => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
            </Select>
          </div>
          <div>
            <Label>Mode</Label>
            <Select value={config.mode} onChange={(e) => setConfig({ ...config, mode: e.target.value as "test" | "live" })}>
              <option value="test">Test mode</option>
              <option value="live">Live mode</option>
            </Select>
          </div>
        </div>
        <div>
          <Label>Default currency</Label>
          <Select value={config.default_currency} onChange={(e) => setConfig({ ...config, default_currency: e.target.value })}>
            {["USD","EUR","GBP","CAD","AUD","JPY"].map((c) => <option key={c} value={c}>{c}</option>)}
          </Select>
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={test} loading={testing} disabled={!connected}>Test connection</Button>
        <Button onClick={save} loading={saving}>Save</Button>
      </div>
    </div>
  );
}
